import { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';
import ReplayOutlined from '@mui/icons-material/ReplayOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import { ComingSoonPanel } from '../components/ComingSoonPanel';
import { CarrierFinancials } from '../features/finance/FinancialsTabs';
import { EditRecordButton } from '../components/EditRecordButton';
import { VaultNotes } from '../components/VaultNotes';
import { ProfileHeader } from '../components/ProfileHeader';
import { ViewCommunicationsButton } from '../features/communications/ViewCommunicationsButton';
import { StatTile } from '../components/StatTile';
import { TileGrid } from '../components/TileGrid';
import {
  InfoPanel,
  OutcomesPanel,
  PerformancePanel,
  RateHistoryPanel,
  RecentActivityPanel,
  RoutesPanel,
  ServicesPanel,
} from '../features/carriers/CarrierPanels';
import { useCarrierStats } from '../features/carriers/stats';
import { useCarriers } from '../features/carriers/useCarriers';
import { ActiveShipmentsTile } from '../features/shipments/ActiveShipmentsTile';
import { ConversationLinksPanel, ShipmentsTabPanel } from '../features/shipments/ShipmentLinkPanels';
import { pct } from '../lib/numbers';

const TABS = [
  'Overview',
  'Rates & Services',
  'Shipments',
  'Schedules',
  'Communications',
  'Financials',
  'Activity History',
] as const;
const GAP = { xs: 1.5, md: 2 };
const FIT = {
  display: 'grid',
  gap: GAP,
  alignItems: 'start',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
};

/** One carrier: who it is, how our shipments with it went, and every rate it gave */
export function CarrierPage() {
  const { id } = useParams();
  const carriers = useCarriers();
  const statsOf = useCarrierStats();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const c = carriers.items.find((x) => x.id === id);
  if (carriers.loading) return null; // still loading: do not decide "not found" yet
  if (!c) return <Navigate to="/carriers" replace />;
  const st = statsOf(c);

  return (
    <Stack spacing={GAP}>
      <ProfileHeader
        back={{ to: '/carriers', label: 'Carriers' }}
        name={c.name}
        status={{ label: `${c.status} Carrier`, tone: c.status === 'Active' ? 'success' : 'neutral' }}
        details={[
          `${c.mode === 'Air' ? 'Air Freight' : c.mode === 'Sea' ? 'Sea Freight' : 'Road'}`,
          c.country,
          c.website,
          c.phone,
          c.email,
        ]}
        tabs={TABS}
        tab={tab}
        onTab={setTab}
        actions={
          <>
            <Button variant="contained" onClick={() => setTab('Shipments')}>
              View Shipments
            </Button>
            <Button variant="contained" onClick={() => setTab('Rates & Services')}>
              View Rates
            </Button>
            <ViewCommunicationsButton />
            <EditRecordButton what="carrier" />
          </>
        }
      />

      {tab === 'Overview' && (
        <Stack spacing={GAP}>
          <TileGrid lg={3} xl={5}>
            <StatTile
              count={st.total}
              title="Total Shipments"
              note="All time"
              icon={<RouteOutlined />}
              tone="info"
              onClick={() => setTab('Shipments')}
            />
            <ActiveShipmentsTile count={st.active} onClick={() => setTab('Shipments')} />
            <StatTile
              count={st.arrived}
              title={`Arrived Shipments (${pct(st.arrived, st.total)}%)`}
              note="Successfully delivered"
              icon={<CheckCircleOutlined />}
              tone="success"
              onClick={() => setTab('Shipments')}
            />
            <StatTile
              count={st.delayed}
              title={`Delayed Shipments (${pct(st.delayed, st.total)}%)`}
              note="Running late"
              icon={<ScheduleOutlined />}
              tone="error"
              onClick={() => setTab('Shipments')}
            />
            <StatTile
              count={st.rolledOver}
              title="Rolled Over"
              note="Bookings pushed to a later sailing or flight"
              icon={<ReplayOutlined />}
              tone="warning"
              onClick={() => setTab('Rates & Services')}
            />
          </TileGrid>
          <Box sx={FIT}>
            <InfoPanel c={c} />
            <Stack spacing={GAP}>
              <PerformancePanel c={c} st={st} />
            </Stack>
            <Stack spacing={GAP}>
              <OutcomesPanel st={st} />
              <ServicesPanel c={c} />
            </Stack>
          </Box>
          <RoutesPanel st={st} />
          <Box sx={FIT}>
            <RateHistoryPanel rates={st.rates} limit={4} />
            <RecentActivityPanel rates={st.rates} />
          </Box>
          <VaultNotes notes={c.notes} />
        </Stack>
      )}
      {tab === 'Rates & Services' && (
        <Stack spacing={GAP}>
          <RoutesPanel st={st} />
          <Box sx={FIT}>
            <RateHistoryPanel rates={st.rates} title="Rate History" />
            <ServicesPanel c={c} />
          </Box>
        </Stack>
      )}
      {tab === 'Shipments' && <ShipmentsTabPanel rows={st.ships} />}
      {tab === 'Schedules' && <ComingSoonPanel title="Schedules" />}
      {tab === 'Communications' && <ConversationLinksPanel ships={st.ships} />}
      {tab === 'Financials' && <CarrierFinancials carrier={c.name} />}
      {tab === 'Activity History' && <RecentActivityPanel rates={st.rates} />}
    </Stack>
  );
}
